import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import PageTransition, { Section } from "@/components/site/PageTransition";
import Seo from "@/components/site/Seo";
import Avatar from "@/components/site/Avatar";

function Carousel({ title, items, eyebrow }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    if (ref.current) {
      ref.current.scrollBy({ left: dir * 400, behavior: "smooth" });
    }
  };
  return (
    <div className="mb-20" data-testid={`carousel-${title.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-end justify-between mb-7 px-6 lg:px-10">
        <div>
          {eyebrow && <div className="label-mono mb-2">{eyebrow}</div>}
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tighter">{title}</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => scroll(-1)} className="glass-btn ghost p-3" aria-label="Previous" data-testid={`carousel-${title.toLowerCase().replace(/\s/g,'-')}-prev`}><ChevronLeft size={16} /></button>
          <button onClick={() => scroll(1)}  className="glass-btn ghost p-3" aria-label="Next"     data-testid={`carousel-${title.toLowerCase().replace(/\s/g,'-')}-next`}><ChevronRight size={16} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-3 sm:gap-5 overflow-x-auto px-6 lg:px-10 pb-4 no-scrollbar carousel-mask scroll-smooth">
        {items.map((a) => (
          <Link
            key={a.id}
            to={`/${a.slug}`}
            className="block flex-none w-[calc(50vw-2rem)] sm:w-[300px] md:w-[340px] rounded-2xl overflow-hidden border border-white/5 lift relative"
            data-testid={`artist-card-${a.slug}`}
          >
            <div style={{ aspectRatio: "3/4" }} className="relative">
              <Avatar src={a.image} name={a.name} className="absolute inset-0 w-full h-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="badge muted">{a.tagline?.split('/')?.[0]?.trim() || "Artist"}</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="font-display text-2xl font-bold tracking-tight">{a.name}</div>
                <div className="label-mono mt-1 text-cyan-300/90">{a.tagline}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Artists() {
  const [artists, setArtists] = useState([]);
  useEffect(() => { api.get("/artists").then((r) => setArtists(r.data)); }, []);

  // group by tag-section (light segmentation)
  const featured = artists;
  const recent   = [...artists].reverse();
  const all      = artists;

  return (
    <PageTransition>
      <Seo title="Artists" description="The Pulse Media roster — voices we amplify across music, film, and streaming." />
      <section className="pt-40 pb-12 px-6 lg:px-10" data-testid="artists-hero">
        <div className="max-w-7xl mx-auto">
          <div className="label-mono mb-4">/ The Roster</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[1.0]">
            Voices we <span className="gradient-text">amplify.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-lg">
            A diverse range of intimate sound — every signing is a long-term partnership across music, video, and brand.
          </p>
        </div>
      </section>

      <Section className="py-12">
        <Carousel title="Featured" items={featured} eyebrow="/ Now playing" />
        <Carousel title="Recently Signed" items={recent} eyebrow="/ Fresh ink" />
        <Carousel title="The Full Roster" items={all} eyebrow="/ Every artist" />
      </Section>

      <Section className="px-6 lg:px-10 py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Want to be next?</h2>
          <p className="mt-5 text-zinc-400">We sign artists we believe in. If that's you, start with distribution — that's how every signing begins.</p>
          <Link to="/distribution" className="glass-btn solid mt-8" data-testid="artists-distro-cta">
            Submit your music <ArrowUpRight size={14} />
          </Link>
        </div>
      </Section>
    </PageTransition>
  );
}
